<template>
  <div
    class="recommendation"
    :class="{
      'recommendation-doubled': doubled,
      [`recommendation-${size}`]: true,
      [`recommendation-${variant}`]: variant,
    }"
  >
    <div class="recommendation-header">
      <span class="title">{{ title }}</span>
      <span class="label">{{ label }}</span>
    </div>

    <div class="recommendation-action">
      {{ action }}

      <SvgIcon
        name="components/link-arrow"
        width="16"
        height="16"
      />
    </div>
  </div>
</template>

<script>
import SvgIcon from 'vue-svgicon/dist/components/svgicon.common'

import '../../icons/components/link-arrow'

export default {
  components: {
    SvgIcon,
  },

  props: {
    /**
     * Text content to place as title.
     */
    title: {
      type: String,
      required: true,
    },

    /**
     * Text content to place as label.
     */
    label: {
      type: String,
      required: true,
    },

    /**
     * Text content to place as action.
     */
    action: {
      type: String,
      default: null,
    },

    /**
     * Applies one of the color variants to the component.
     */
    variant: {
      type: String,
      default: 'gray',
      validator: (value) => ['gray', 'red', 'green', 'yellow'].indexOf(value) !== -1,
    },

    /**
     * Set the size of the component's appearance. 'md' (default), or 'lg'.
     */
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['md', 'lg'].indexOf(value) !== -1,
    },

    /**
     * When set, the action text will be displayed on hovering the component.
     */
    doubled: {
      type: Boolean,
      default: false,
    },
  },

  mounted () {
    if (this.doubled && !this.action) {
      throw new Error('The `action` must be defined when `doubled` is set to true.')
    }
  },
}
</script>

<style lang="scss" scoped>


$colors: (
  "gray": #95b1be,
  "red": #ff6256,
  "green": #00beb1,
  "yellow": #f3ba3d,
);

.recommendation {
  border-radius: 40px;
  border: 2px solid $gray-900;
  padding: 0 1rem;
  font-size: $font-size-sm;
  height: 40px;

  @each $color, $value in $colors {
    &.recommendation-#{$color} {
      border-color: $value;

      .recommendation-header {
        .label {
          border-left: 2px solid $value;
        }
      }

      &.recommendation-doubled:hover {
        background-color:  $value;
      }
    }
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    overflow: hidden;

    .title,
    .label {
      padding: 0.375rem 0;
      height: 40px;
      justify-content: space-between;
      align-items: center;
      display: inline-flex;
    }

    .title {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      position: relative;
    }

    .label {
      padding-left: 0.75rem;
    }
  }

  &-action {
    display: none;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    cursor: pointer;
  }

  &-doubled {
    &:hover {
      .recommendation-header {
        display: none;
      }

      .recommendation-action {
        display: flex;
      }
    }

    .recommendation-header {
      .label,
      .title {
        height: auto;
      }

      .label {
        border-left: none !important;
      }
    }
  }

  &-lg {
    height: 64px;
    padding: 0;

    .recommendation-header,
    .recommendation-action {
      margin: 0 1.5rem;
    }

    .recommendation-header {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      .title,
      .label {
        width: 100%;
        max-width: 100%;
        display: inline-block;
        padding: 0;
      }

      .title {
        padding: 0;
      }
    }
  }

  .label {
    font-size: $font-size-base;
    font-weight: bold;
    text-transform: uppercase;
  }
}
</style>
